import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Leaderboard as MUILeaderboard } from '@mui/icons-material';
import useExplorePageStyles from '../useExplorePageStyles';
import useLeaderboardStyles from './useLeaderboardStyles';
import { AnswerWithQid, Question, User } from '../../../../types';
import usePageLink from '../../../../hooks/useQuestion';
import { ModelType, MODELTYPES, setRankingType } from '../../../../redux/slices/exploreSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import useGlobalStyles from '../../../useGlobalStyles';
import { currentUserSelector } from '../../../../redux/slices/userSlice';
import {
  setChallenged,
  setChallengeModalOpen,
  setGameResult,
} from '../../../../redux/slices/gameParrotSlice';
import {
  getAnswersWithScore,
  getQuestionsWithScore,
  getUsersWithUpdatedScore,
} from '../exploreUtils';

/**
 * Interface representing the props for the Leaderboard component.
 *
 * - users The current full list of users.
 * - questions The current full list of questions.
 * - answers The current full list of answers with their question's IDs.
 * - rankingType The current ranking type that the leaderboard is displaying.
 * - openChallenge Function to open the challenge modal when challenging a user.
 */
interface LeaderboardProps {
  users: User[];
  questions: Question[];
  answers: AnswerWithQid[];
  rankingType: ModelType;
}

/**
 * Leaderboard component renders a leaderboard on the explore page which can display
 * rankings for users, questions, and answers.
 *
 * @param users The current full list of users.
 * @param questions The current full list of questions.
 * @param answers The current full list of answers with their question's IDs.
 * @param rankingType The current ranking type that the leaderboard is displaying.
 */
const Leaderboard = ({ users, questions, answers, rankingType }: LeaderboardProps) => {
  const { classes } = useExplorePageStyles();
  const { classes: leaderboardClasses } = useLeaderboardStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const { handleAnswer } = usePageLink();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);

  /**
   * Function to get the background color for an entry in the leaderboard.
   *
   * @param idx - The entry's placing in the leaderboard.
   * @returns the background color, or an empty string if the entry isn't in the top 3.
   */
  const getBackground = (idx: number) => {
    if (idx === 0) return 'gold';
    if (idx === 1) return 'silver';
    if (idx === 2) return '#cd7f32';
    return '';
  };

  return (
    <div className={leaderboardClasses.leaderboardContainerRow}>
      <div className={leaderboardClasses.leaderboardContainerColumn}>
        <div className={leaderboardClasses.titleAndBoardContainer}>
          <div className={classes.titleContainer}>
            <span>Leaderboard</span>
            <div style={{ width: '10px' }}></div>
            <MUILeaderboard />
          </div>
          <TableContainer>
            <Table stickyHeader>
              <TableHead style={{ width: '100px' }}>
                <TableRow>
                  <TableCell align='left' className={leaderboardClasses.headerTableCell}>
                    Rank
                  </TableCell>
                  <TableCell align='left' className={leaderboardClasses.headerTableCell}>
                    {rankingType}
                  </TableCell>
                  <TableCell
                    align='right'
                    className={leaderboardClasses.headerTableCell}></TableCell>
                  <TableCell align='right' className={leaderboardClasses.headerTableCell}>
                    Score
                  </TableCell>
                </TableRow>
              </TableHead>
              {rankingType === ModelType.UserT && (
                <TableBody>
                  {getUsersWithUpdatedScore(users, questions, answers, 'overall')
                    .toSorted((a, b) => b.score - a.score)
                    .map((user, idx) => (
                      <TableRow
                        key={idx}
                        className={leaderboardClasses.tableRow}
                        onClick={() => navigate(`/profile/${user.username}`)}>
                        <TableCell
                          align='left'
                          width={1}
                          style={{ background: getBackground(idx), paddingLeft: '28px' }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell align='left'>
                          <div className={leaderboardClasses.userTableCell}>
                            <Avatar
                              className={globalClasses.userAvatar}
                              src={`${process.env.PUBLIC_URL}/assets/profiles/${user.profilePicture}`}
                            />
                            <span className={leaderboardClasses.userTableCellUsername}>
                              {user.username}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell align='right'></TableCell>
                        <TableCell align='right' className={leaderboardClasses.gameParrotCell}>
                          {user._id !== currentUser?._id && (
                            <Tooltip
                              title={`Challenge ${user.username}`}
                              placement='top'
                              classes={{ tooltip: globalClasses.tooltip }}
                              PopperProps={{
                                modifiers: [
                                  {
                                    name: 'offset',
                                    options: {
                                      offset: [0, -10],
                                    },
                                  },
                                ],
                              }}>
                              <img
                                className={leaderboardClasses.gameParrotIcon}
                                onClick={e => {
                                  e.stopPropagation();
                                  dispatch(setChallenged(user));
                                  dispatch(setGameResult(['incomplete']));
                                  dispatch(setChallengeModalOpen(true));
                                }}
                                src={`${process.env.PUBLIC_URL}/assets/branding/gameparrot.png`}
                              />
                            </Tooltip>
                          )}
                          <p style={{ minWidth: '50px' }}>{user.score}</p>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
              {rankingType === ModelType.QuestionT && (
                <TableBody>
                  {getQuestionsWithScore(questions, 'overall')
                    .toSorted((a, b) => b.score - a.score)
                    .map((question, idx) => (
                      <TableRow
                        key={idx}
                        className={leaderboardClasses.tableRow}
                        onClick={() => {
                          if (question._id) {
                            handleAnswer(question._id);
                          }
                        }}>
                        <TableCell
                          align='left'
                          width={1}
                          style={{ background: getBackground(idx), paddingLeft: '28px' }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell align='left'>{question.title}</TableCell>
                        <TableCell align='right' />
                        <TableCell align='right' style={{ paddingRight: '30px' }}>
                          {question.score}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
              {rankingType === ModelType.AnswerT && (
                <TableBody>
                  {getAnswersWithScore(answers, 'overall')
                    .toSorted((a, b) => b.score - a.score)
                    .map((answer, idx) => (
                      <TableRow
                        key={idx}
                        className={leaderboardClasses.tableRow}
                        onClick={() => {
                          if (answer.qid) {
                            handleAnswer(answer.qid);
                          }
                        }}>
                        <TableCell
                          align='left'
                          width={1}
                          style={{ background: getBackground(idx), paddingLeft: '28px' }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell
                          align='left'
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipses',
                          }}>
                          {answer.text}
                        </TableCell>
                        <TableCell align='right' />
                        <TableCell align='right' style={{ paddingRight: '30px' }}>
                          {answer.score}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </div>
        <ToggleButtonGroup
          value={rankingType}
          exclusive
          onChange={(event, newType) => {
            if (newType !== null) {
              dispatch(setRankingType(newType));
            }
          }}
          aria-label='ranking type'>
          {MODELTYPES.map((type, idx) => (
            <ToggleButton
              className={leaderboardClasses.toggleButton}
              key={idx}
              value={type}
              selected={rankingType === type}
              onClick={() => {
                setRankingType(type);
              }}>
              {type}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
    </div>
  );
};

export default Leaderboard;
